import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrandMark, BrandMarkLarge } from '@/components/Logo';
import SectionIcon from '@/components/SectionIcon';
import { I } from '@/lib/icons';
import { useWorkspace } from '@/hooks/useData';
import { useToast } from '@/components/Toast';
import { useAuth } from '@/contexts/AuthContext';

export default function Onboarding() {
  const navigate = useNavigate();
  const toast = useToast();
  const { create } = useWorkspace();
  const { signOut, user } = useAuth();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({
    type: 'szco',
    name: '',
    ico: '',
    dic: '',
    ic_dph: '',
    address: '',
    iban: '',
    bank: '',
    is_dph_payer: false,
  });
  const totalSteps = 4;

  const update = (patch) => setProfile(p => ({ ...p, ...patch }));

  const validateStep = () => {
    if (step === 1) {
      if (!profile.type) return 'Vyber typ firmy.';
    }
    if (step === 2) {
      if (!profile.name?.trim()) return 'Zadaj názov firmy.';
      if (!profile.ico?.trim()) return 'Zadaj IČO.';
    }
    if (step === 3) {
      if (!profile.iban?.trim()) return 'Zadaj IBAN.';
    }
    return null;
  };

  const next = async () => {
    const err = validateStep();
    if (err) { toast.error(err); return; }
    if (step < totalSteps - 1) { setStep(step + 1); return; }
    // last step — save
    setSaving(true);
    try {
      await create({
        name: profile.name.trim(),
        type: profile.type,
        ico: profile.ico.trim(),
        dic: profile.dic.trim(),
        ic_dph: profile.ic_dph.trim() || null,
        address: profile.address.trim() || null,
        iban: profile.iban.replace(/\s+/g, ''),
        bank: profile.bank.trim() || null,
        is_dph_payer: !!profile.is_dph_payer,
        default_dph_rate: profile.is_dph_payer ? 23 : 0,
      });
      toast.success('Účet pripravený — vystav prvú faktúru.');
      navigate('/', { replace: true });
    } catch (e) {
      toast.error(e.message || 'Nepodarilo sa uložiť firmu.');
    } finally { setSaving(false); }
  };

  const back = () => step > 0 && setStep(step - 1);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1, background: 'var(--bg)',
      display: 'flex', flexDirection: 'column',
      animation: 'cd-fadeIn 0.3s var(--ease)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '20px 28px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BrandMark size={28} />
          <div style={{ fontSize: 14, fontWeight: 600 }}>podnikai.sk</div>
        </div>
        <button onClick={signOut} style={{
          background: 'transparent', border: 'none', color: 'var(--text-muted)',
          fontSize: 13, cursor: 'pointer', padding: 6, fontFamily: 'inherit',
        }}>Odhlásiť ({user?.email})</button>
      </div>

      {/* Progress dots */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div key={i} style={{
            width: i === step ? 24 : 8, height: 8, borderRadius: 4,
            background: i <= step ? 'var(--product-primary)' : 'var(--border-strong)',
            transition: 'all 0.3s var(--ease)',
          }} />
        ))}
      </div>

      {/* Body */}
      <div className="cd-scroll" style={{ flex: 1, overflowY: 'auto', display: 'flex', justifyContent: 'center', padding: '0 28px 28px' }}>
        <div style={{ width: 540, maxWidth: '100%', display: 'flex', alignItems: 'center' }}>
          {step === 0 && <Welcome />}
          {step === 1 && <TypeStep profile={profile} update={update} />}
          {step === 2 && <CompanyStep profile={profile} update={update} />}
          {step === 3 && <BankStep profile={profile} update={update} />}
        </div>
      </div>

      {/* Footer */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '20px 28px', borderTop: '1px solid var(--border)', background: 'var(--bg-surface)',
      }}>
        <button onClick={back} disabled={step === 0} style={{
          background: 'transparent', border: 'none',
          color: step === 0 ? 'var(--text-muted)' : 'var(--text-primary)',
          opacity: step === 0 ? 0.4 : 1,
          fontSize: 13, padding: '8px 12px', cursor: step === 0 ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit',
        }}><I.chevL /> Späť</button>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Krok {step + 1} z {totalSteps}</div>
        <button onClick={next} disabled={saving}
          data-testid="onboarding-next"
          className="cd-btn cd-btn-primary">
          {saving ? <><span className="cd-spinner" style={{ borderTopColor: '#fff' }} /> Ukladám...</>
            : step === totalSteps - 1
              ? <>Hotovo <I.check /></>
              : <>Pokračovať <I.chevR /></>}
        </button>
      </div>
    </div>
  );
}

function Welcome() {
  return (
    <div style={{ textAlign: 'center', width: '100%' }}>
      <div style={{ display: 'inline-block', marginBottom: 22 }}><BrandMarkLarge size={88} /></div>
      <h1 style={{
        margin: '0 0 12px', fontSize: 38, fontWeight: 700,
        fontFamily: 'var(--font-display)', letterSpacing: '-0.025em', lineHeight: 1.05,
      }}>Vitaj.</h1>
      <p style={{
        margin: '0 auto', fontSize: 15, color: 'var(--text-secondary)',
        lineHeight: 1.55, maxWidth: 420,
      }}>
        Fakturácia pre slovenských živnostníkov a s.r.o. —
        nastavíme to za 60 sekúnd. Žiadne mesačné poplatky, vlastné dáta.
      </p>
      <div style={{ display: 'flex', justifyContent: 'center', gap: 10, marginTop: 28 }}>
        {[
          { color: 'green',  icon: 'doc',     label: 'Faktúra → PDF' },
          { color: 'orange', icon: 'receipt', label: 'Výdavky' },
          { color: 'pink',   icon: 'users',   label: 'Klienti' },
        ].map((f, i) => (
          <div key={i} style={{
            padding: '12px 14px', background: 'var(--bg-surface)',
            border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <SectionIcon color={f.color} size={26}>
              {React.createElement(I[f.icon], { width: 13, height: 13 })}
            </SectionIcon>
            <span style={{ fontSize: 12.5, fontWeight: 600 }}>{f.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function TypeStep({ profile, update }) {
  const opts = [
    { id: 'szco', label: 'SZČO', desc: 'Živnostník · SR paušál, daň typu B', icon: 'users' },
    { id: 'sro',  label: 's.r.o.', desc: 'Spoločnosť · DPPO 21%, možná platca DPH', icon: 'bank' },
  ];
  return (
    <div style={{ width: '100%' }}>
      <h2 style={{
        margin: '0 0 6px', fontSize: 26, fontWeight: 700,
        fontFamily: 'var(--font-display)', letterSpacing: '-0.02em', textAlign: 'center',
      }}>Akú firmu vystavuješ?</h2>
      <p style={{ margin: '0 0 24px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
        Vyber typ firmy. Môžeš zmeniť neskôr v Nastaveniach.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {opts.map(o => {
          const active = profile.type === o.id;
          return (
            <button key={o.id}
              data-testid={`onb-type-${o.id}`}
              onClick={() => update({ type: o.id })}
              style={{
                padding: '20px 18px',
                background: active ? 'var(--product-primary-soft)' : 'var(--bg-surface)',
                border: `2px solid ${active ? 'var(--product-primary)' : 'var(--border)'}`,
                borderRadius: 'var(--r-lg)', cursor: 'pointer', textAlign: 'left',
                display: 'flex', flexDirection: 'column', gap: 12, fontFamily: 'inherit',
              }}>
              <SectionIcon color="green" size={36}>
                {React.createElement(I[o.icon], { width: 18, height: 18 })}
              </SectionIcon>
              <div>
                <div style={{
                  fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-display)',
                  color: active ? 'var(--product-primary-hover)' : 'var(--text-primary)',
                }}>{o.label}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{o.desc}</div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CompanyStep({ profile, update }) {
  return (
    <div style={{ width: '100%' }}>
      <h2 style={{
        margin: '0 0 6px', fontSize: 26, fontWeight: 700,
        fontFamily: 'var(--font-display)', letterSpacing: '-0.02em', textAlign: 'center',
      }}>Êdaje o firme</h2>
      <p style={{ margin: '0 0 22px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
        Tieto údaje pôjdu do hlavičky každej faktúry.
      </p>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <OnbField label="Názov firmy" full value={profile.name} onChange={v => update({ name: v })}
          placeholder={profile.type === 'sro' ? 'Moja firma s.r.o.' : 'Meno Priezvisko'}
          testId="onb-name" />
        <OnbField label="Adresa" full value={profile.address} onChange={v => update({ address: v })}
          placeholder="Hlavná 1, 811 01 Bratislava" testId="onb-address" />
        <OnbField label="IČO" mono value={profile.ico} onChange={v => update({ ico: v })}
          placeholder="12345678" testId="onb-ico" />
        <OnbField label="DIČ" mono value={profile.dic} onChange={v => update({ dic: v })}
          placeholder="1080123456" />
        <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
          <input type="checkbox" id="is_dph_payer" checked={profile.is_dph_payer}
            onChange={e => update({ is_dph_payer: e.target.checked })}
            style={{ width: 18, height: 18, cursor: 'pointer' }} />
          <label htmlFor="is_dph_payer" style={{ fontSize: 13, cursor: 'pointer' }}>
            Som platca DPH (mám IČ DPH)
          </label>
        </div>
        {profile.is_dph_payer && (
          <OnbField label="IČ DPH" mono full value={profile.ic_dph}
            onChange={v => update({ ic_dph: v })} placeholder="SK1080123456" />
        )}
      </div>
    </div>
  );
}

function BankStep({ profile, update }) {
  return (
    <div style={{ width: '100%' }}>
      <h2 style={{
        margin: '0 0 6px', fontSize: 26, fontWeight: 700,
        fontFamily: 'var(--font-display)', letterSpacing: '-0.02em', textAlign: 'center',
      }}>Bankový účet</h2>
      <p style={{ margin: '0 0 22px', fontSize: 13, color: 'var(--text-muted)', textAlign: 'center' }}>
        IBAN sa zobrazí na faktúrach pri inštrukciách na úhradu.
      </p>
      <div style={{ display: 'grid', gap: 12 }}>
        <OnbField label="IBAN" mono full value={profile.iban}
          onChange={v => update({ iban: v })}
          placeholder="SK89 1100 0000 0029 4501 8765" testId="onb-iban" />
        <OnbField label="Banka (voliteľné)" full value={profile.bank}
          onChange={v => update({ bank: v })} placeholder="Tatra banka, a.s." />
      </div>
      <div style={{
        marginTop: 22, padding: 14, background: 'var(--success-dim)',
        borderRadius: 'var(--r-md)', display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <SectionIcon color="green" size={28}>
          {React.createElement(I.check, { width: 14, height: 14 })}
        </SectionIcon>
        <div style={{ fontSize: 12.5, color: '#1a6e4f' }}>
          Skoro hotovo — klikni na <strong>Hotovo</strong> a vystav prvú faktúru.
        </div>
      </div>
    </div>
  );
}

function OnbField({ label, value, onChange, mono, full, placeholder, testId }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5,
      }}>{label}</div>
      <input className={`cd-input${mono ? ' mono' : ''}`} data-testid={testId}
        value={value || ''} onChange={e => onChange?.(e.target.value)}
        placeholder={placeholder}
        style={{ padding: '11px 12px', fontSize: 14 }} />
    </div>
  );
}
